import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { setupAuthInterceptor, retryWithBackoff, deduplicateRequest } from '../common';
import { NAMESPACES } from '../configuration';

const BASE_URL = NAMESPACES.payments_api.dte;

// Helper function to configure the Axios instance
const api: AxiosInstance = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Types
export interface ReceptorData {
  dte_type?: number;
  rut: string;
  razon_social?: string;
  direccion?: string;
  comuna?: string;
  giro?: string;
  contacto?: string;
  forma_pago?: number;
}

// Minimal payload for Boleta Electrónica (tipo 39). Boletas do not require receptor
// data; the backend defaults RUT to the anonymous consumer (66666666-6).
export interface BoletaPayload {
  dte_type: 39;
  indicador_servicio: number;
}

export interface DteRecord {
  dte_id: number;
  receipt_id: number;
  local_id: number;
  tipo_dte: number;
  folio: number | null;
  track_id: string | null;
  sii_generated: boolean;
  sii_envelope_created: boolean;
  sent_to_sii: boolean;
  sii_accepted: boolean;
  sii_rejected: boolean;
  failed: boolean;
  sii_rejection_reason: string | null;
  sii_response: string | null;
  created_at: string;
  updated_at: string;
  dte_xml?: string;
  envelope_xml?: string;
  sii_response_full?: Record<string, unknown>;
}

export interface GenerateDteResponse {
  dte_record_id: number;
  folio: number;
  track_id: string;
  sii_response: Record<string, unknown>;
  dte_xml: string;
  envelope_xml: string;
}

export interface GetDtesResponse {
  success: boolean;
  count: number;
  dtes: DteRecord[];
}

export interface QueryDteStateResponse {
  success: boolean;
  estado: string;
  ok: boolean;
  glosaEstado: string;
  descripcion: string;
  responseXml: string;
  query: {
    rutEmisor: string;
    rutReceptor: string;
    folio: number;
    total: number;
    fechaDte: string;
    tipoDte: number;
  };
  dte_info: {
    dte_id: number;
    receipt_id: number;
    local_id: number;
    track_id: string;
  };
}

export interface GeneratePdfResponse {
  success: boolean;
  message: string;
  dte_id: number;
  folio: number;
  tipo_dte: number;
  formato: string;
  version: string;
  cedible: boolean;
  pdf_size: number;
  content_type: string;
  pdf_content_base64: string;
}

/**
 * 
 * # Parser for generating DTE from receipt
generate_dte_parser = reqparse.RequestParser()
generate_dte_parser.add_argument('dte_type', type=int, required=False, default=33, help='Tipo de DTE a generar: 33=Factura, 34=Factura Exenta, 39=Boleta')
generate_dte_parser.add_argument('rut', type=str, required=True, help='RUT del receptor (con guión y dígito verificador)')
generate_dte_parser.add_argument('razon_social', type=str, required=False, help='Razón social del receptor')
generate_dte_parser.add_argument('direccion', type=str, required=False, help='Dirección del receptor')
generate_dte_parser.add_argument('comuna', type=str, required=False, help='Comuna del receptor')
generate_dte_parser.add_argument('giro', type=str, required=False, help='Giro comercial del receptor')
generate_dte_parser.add_argument('contacto', type=str, required=False, help='Teléfono de contacto del receptor')
generate_dte_parser.add_argument('forma_pago', type=int, required=False, default=1, help='Forma de pago: 1=Contado, 2=Crédito, 3=Sin costo')

@ns.route('/GenerateDteForReceipt/<int:local_id>/<int:receipt_id>')
@ns.param('local_id', 'ID del local que genera el DTE')
@ns.param('receipt_id', 'ID del recibo para el cual se generará el DTE')
class GenerateDteForReceipt(Resource):
    @ns.doc('generate_dte_for_receipt',
            description='**GENERA DOCUMENTOS TRIBUTARIOS ELECTRÓNICOS END-TO-END** a partir de un recibo de pago. '
                       'Este endpoint ejecuta el flujo completo: verificar CAF → generar DTE → crear sobre → enviar a SII. '
                       'El proceso incluye gestión automática de folios, construcción del documento, '
                       'timbraje electrónico, firma digital y envío al SII. '
                       'Retorna el DTE completo con track ID del SII.',
            responses={
                200: 'DTE generado y enviado exitosamente - retorna datos completos del DTE',
                400: 'Parámetros inválidos, tipo de DTE no soportado o no hay folios disponibles',
                404: 'Recibo no encontrado o certificado no disponible',
                500: 'Error en el proceso (generación, sobre o envío)',
                501: 'Tipo de DTE no implementado'
            })
    @ns.expect(generate_dte_parser)
    @jwt_required()
    def post(self, local_id, receipt_id):
        '''
        Genera un Documento Tributario Electrónico (DTE) con flujo completo end-to-end.
        
        **FLUJO COMPLETO AUTOMATIZADO:**
        1. Verifica si existe CAF con folios disponibles
        2. Si no hay folios, solicita nuevos automáticamente del SII
        3. Genera el DTE con el folio correspondiente
        4. Crea el sobre (envelope) para envío
        5. Envía el sobre al SII
        6. Actualiza registros en MySQL y DynamoDB
        7. Retorna toda la información del proceso
        
        **BASE DE DATOS:**
        - Crea registro en tabla `dtes` al inicio
        - Actualiza estado en cada fase del proceso
        - Permite rastrear exactamente dónde está cada DTE
        
        Tipos de DTE soportados:
        - 33: Factura Electrónica (default) - IMPLEMENTADO
        - 34: Factura No Afecta o Exenta - PRÓXIMAMENTE
        - 39: Boleta Electrónica - PRÓXIMAMENTE
        
        Parámetros del body:
        - dte_type: Tipo de DTE (opcional, default=33)
        - rut: RUT del cliente (REQUERIDO, con guión: ej. 12345678-9)
        - razon_social: Nombre o razón social del cliente
        - direccion: Dirección del cliente
        - comuna: Comuna del cliente
        - giro: Actividad comercial del cliente
        - contacto: Teléfono del cliente
        - forma_pago: 1=Contado (default), 2=Crédito, 3=Sin costo
        
        **RESPUESTA:**
        Retorna objeto completo con:
        - dte_record_id: ID del registro en MySQL
        - folio: Número de folio asignado
        - track_id: ID de seguimiento del SII
        - sii_response: Respuesta completa del SII
        - dte_xml: XML del DTE generado
        - envelope_xml: XML del sobre enviado
        '''
        try:
            current_app.logger.info("=" * 80)
            current_app.logger.info(f"NEW DTE REQUEST - Local: {local_id}, Receipt: {receipt_id}")
            current_app.logger.info("=" * 80)
            
            args = generate_dte_parser.parse_args()
            
            # Prepare receptor data
            receptor_data = {
                'rut': args['rut'],
                'razon_social': args.get('razon_social', 'Cliente'),
                'direccion': args.get('direccion', 'Sin dirección'),
                'comuna': args.get('comuna', 'Sin comuna'),
                'giro': args.get('giro', 'Sin giro'),
                'contacto': args.get('contacto', ''),
                'forma_pago': args.get('forma_pago', 1),
                'dte_type': args.get('dte_type', 33)
            }
            
            current_app.logger.info(f"Receptor data: {receptor_data}")
            
            # Validate receipt exists
            receipt = db.session.query(PaymentReceipt).filter_by(receipt_id=receipt_id).first()
            if not receipt:
                current_app.logger.error(f"Receipt {receipt_id} not found")
                return {'error': f'Receipt {receipt_id} not found'}, 404
            
            current_app.logger.info(f"✓ Receipt {receipt_id} found - Amount: ${receipt.subtotal}")
            
            # VALIDATION 1: Ensure receipt.local_id matches the passed local_id
            if receipt.local_id != local_id:
                error_msg = f"Receipt local_id ({receipt.local_id}) does not match requested local_id ({local_id})"
                current_app.logger.error(error_msg)
                return {'error': error_msg}, 400
            
            current_app.logger.info(f"✓ Receipt local_id matches requested local_id: {local_id}")
            
            # Initialize helper to check for existing DTEs
            helper = DteHelper(local_id=local_id)
            
            # VALIDATION 2: Check if an accepted DTE already exists for this receipt and tipo_dte
            tipo_dte = receptor_data['dte_type']
            existing_dte = helper.loaderController.check_existing_accepted_dte(receipt_id, tipo_dte)
            
            if existing_dte:
                error_msg = (f"An accepted DTE already exists for receipt {receipt_id} with tipo_dte {tipo_dte}. "
                            f"DTE ID: {existing_dte['dte_id']}, Folio: {existing_dte['folio']}")
                current_app.logger.error(error_msg)
                return {
                    'error': error_msg,
                    'existing_dte': existing_dte
                }, 409  # 409 Conflict
            
            current_app.logger.info(f"✓ No existing accepted DTE found for receipt {receipt_id}, tipo_dte {tipo_dte}")
            
            # Create DTE record in database FIRST
            current_app.logger.info("Creating DTE record in database...")
            
            try:
                dte_record = Dte(
                    receipt_id=receipt_id,
                    local_id=local_id,
                    tipo_dte=receptor_data['dte_type'],
                    folio=None,  # Will be set during generation
                    sii_generated=False,
                    sii_envelope_created=False,
                    track_id=None,
                    failed=False,
                    sent_to_sii=False,
                    sii_accepted=False,
                    sii_rejected=False
                )
                db.session.add(dte_record)
                db.session.commit()
                
                dte_record_id = dte_record.dte_id
                current_app.logger.info(f"✓ DTE record created - ID: {dte_record_id}")
                
            except Exception as db_error:
                current_app.logger.error(f"Failed to create DTE record: {str(db_error)}")
                db.session.rollback()
                return {'error': 'Failed to create DTE record', 'details': str(db_error)}, 500
            
            # Execute complete workflow (helper already initialized above)
            current_app.logger.info("Starting complete DTE workflow...")
            success, data, status = helper.process_dte_for_receipt_complete(
                receipt_id=receipt_id,
                receptor_data=receptor_data,
                dte_record_id=dte_record_id
            )
            
            if not success:
                current_app.logger.error(f"DTE workflow failed: {data}")
                return data, status
            
            current_app.logger.info("=" * 80)
            current_app.logger.info("✓ DTE WORKFLOW COMPLETED SUCCESSFULLY")
            current_app.logger.info("=" * 80)
            
            return data, status
            
        except Exception as e:
            current_app.logger.error(f"Unexpected error in endpoint: {str(e)}", exc_info=True)
            return {'error': 'Unexpected error', 'details': str(e)}, 500


 */

export const generateDteForReceipt = async (
  local_id: number,
  receipt_id: number,
  receptor_data: ReceptorData | BoletaPayload
): Promise<AxiosResponse<GenerateDteResponse>> => {
  return await retryWithBackoff(() => api.post<GenerateDteResponse>(
    `/GenerateDteForReceipt/${local_id}/${receipt_id}`,
    receptor_data
  ));
};

/**
 * # Parser for getting DTEs by receipt
get_dtes_parser = reqparse.RequestParser()
get_dtes_parser.add_argument('include_xml', type=bool, required=False, default=True,
                             help='Include XML content from DynamoDB (default: True)')


@ns.route('/GetDtesByReceiptId/<int:local_id>/<int:receipt_id>')
@ns.param('local_id', 'ID del local')
@ns.param('receipt_id', 'ID del recibo')
class GetDtesByReceiptId(Resource):
    @ns.doc('get_dtes_by_receipt',
            description='**OBTIENE TODOS LOS DTES DE UN RECIBO** con información completa. '
                       'Este endpoint consulta tanto MySQL (estado, folios, track_id) como '
                       'DynamoDB (XMLs, respuestas SII completas) para retornar una vista '
                       'comprehensiva de todos los DTEs asociados a un recibo. '
                       'Útil para debugging, auditoría y visualización del estado completo.',
            responses={
                200: 'DTEs obtenidos exitosamente - retorna lista completa',
                404: 'Recibo no encontrado',
                500: 'Error al cargar DTEs'
            })
    @ns.expect(get_dtes_parser)
    @jwt_required()
    def get(self, local_id, receipt_id):
        '''
        Obtiene todos los DTEs asociados a un recibo con información completa.
        
        **INFORMACIÓN RETORNADA:**
        
        Cada DTE incluye datos de ambas fuentes:
        
        **Desde MySQL (dtes table):**
        - dte_id: ID del registro
        - receipt_id: ID del recibo asociado
        - local_id: ID del local
        - tipo_dte: Tipo de documento (33=Factura, 39=Boleta, etc.)
        - folio: Número de folio asignado
        - track_id: ID de seguimiento del SII
        - sii_generated: ¿DTE generado? (boolean)
        - sii_envelope_created: ¿Sobre creado? (boolean)
        - sent_to_sii: ¿Enviado al SII? (boolean)
        - sii_accepted: ¿Aceptado por SII? (boolean)
        - sii_rejected: ¿Rechazado por SII? (boolean)
        - failed: ¿Falló el proceso? (boolean)
        - sii_rejection_reason: Razón de rechazo (string)
        - sii_response: Respuesta resumida del SII (string)
        - created_at: Fecha de creación
        - updated_at: Última actualización
        
        **Desde DynamoDB (mp_dte_data table) - si include_xml=True:**
        - dte_xml: XML completo del DTE generado
        - envelope_xml: XML completo del sobre enviado
        - sii_response_full: Respuesta completa del SII (JSON)
        
        **CASOS DE USO:**
        
        1. **Debugging**: Ver estado completo del proceso de generación
        2. **Auditoría**: Revisar historial de DTEs para un recibo
        3. **Retry Logic**: Identificar dónde falló para reintentar
        4. **Visualización**: Mostrar estado al usuario
        5. **Reporting**: Generar reportes de emisión
        
        **PARÁMETROS:**
        - include_xml (query param): Incluir XMLs de DynamoDB (default: true)
        
        **EJEMPLO DE RESPUESTA:**
        ```json
        {
          "success": true,
          "count": 2,
          "dtes": [
            {
              "dte_id": 123,
              "receipt_id": 456,
              "local_id": 1,
              "tipo_dte": 33,
              "folio": 45,
              "track_id": "ABC123XYZ",
              "sii_generated": true,
              "sii_envelope_created": true,
              "sent_to_sii": true,
              "sii_accepted": true,
              "sii_rejected": false,
              "failed": false,
              "sii_rejection_reason": null,
              "sii_response": "...",
              "created_at": "2025-10-19T10:30:00",
              "updated_at": "2025-10-19T10:35:00",
              "dte_xml": "<DTE>...</DTE>",
              "envelope_xml": "<EnvioDTE>...</EnvioDTE>",
              "sii_response_full": {"status": "OK", "trackId": "ABC123XYZ"}
            },
            {
              "dte_id": 124,
              "receipt_id": 456,
              "local_id": 1,
              "tipo_dte": 61,
              "folio": 12,
              "track_id": "XYZ789ABC",
              "sii_generated": true,
              "sii_envelope_created": true,
              "sent_to_sii": false,
              "sii_accepted": false,
              "sii_rejected": true,
              "failed": true,
              "sii_rejection_reason": "RUT Receptor Inválido",
              "sii_response": "...",
              "created_at": "2025-10-19T11:00:00",
              "updated_at": "2025-10-19T11:05:00",
              "dte_xml": "<DTE>...</DTE>",
              "envelope_xml": "<EnvioDTE>...</EnvioDTE>",
              "sii_response_full": {"error": "RUT Receptor Inválido"}
            }
          ]
        }
        ```
        
        **INTERPRETACIÓN DE ESTADOS:**
        
        - sii_generated=True, sii_envelope_created=False:
          → DTE generado, pero sobre falló. DTE XML disponible en DynamoDB.
        
        - sii_generated=True, sii_envelope_created=True, sent_to_sii=False:
          → DTE y sobre listos, pero envío falló. Ambos XMLs en DynamoDB.
        
        - sent_to_sii=True, sii_accepted=False, sii_rejected=False:
          → Enviado al SII, pendiente de validación. Usar track_id para consultar.
        
        - sii_accepted=True:
          → Proceso completado exitosamente. DTE válido y registrado.
        
        - sii_rejected=True:
          → SII rechazó el DTE. Ver sii_rejection_reason para detalles.
        
        - failed=True:
          → Error en el proceso. Ver sii_rejection_reason para detalles.
        
        **NOTAS:**
        - Los DTEs se retornan ordenados por fecha de creación (más recientes primero)
        - Si include_xml=false, los campos dte_xml, envelope_xml y sii_response_full serán null
        - Un recibo puede tener múltiples DTEs (ej: factura original + notas de crédito)
        '''
        try:
            args = get_dtes_parser.parse_args()
            include_xml = args.get('include_xml', True)
            
            current_app.logger.info(f"Getting DTEs for receipt_id={receipt_id}, local_id={local_id}, include_xml={include_xml}")
            
            # Initialize helper
            helper = DteHelper(local_id=local_id)
            
            # Get DTEs
            success, data, status = helper.get_dtes_for_receipt(receipt_id, include_xml)
            
            if not success:
                current_app.logger.error(f"Failed to get DTEs: {data}")
                return data, status
            
            current_app.logger.info(f"Successfully retrieved {data.get('count', 0)} DTE(s)")
            return data, status
            
        except Exception as e:
            current_app.logger.error(f"Error in GetDtesByReceiptId: {str(e)}", exc_info=True)
            return {'error': 'Failed to retrieve DTEs', 'details': str(e)}, 500


 */

export const getDtesByReceiptId = async (
  local_id: number,
  receipt_id: number,
  include_xml: boolean = true
): Promise<AxiosResponse<GetDtesResponse>> => {
  return await deduplicateRequest(
    `getDtesByReceiptId:${local_id}:${receipt_id}:${include_xml}`,
    () => retryWithBackoff(() => api.get<GetDtesResponse>(
      `/GetDtesByReceiptId/${local_id}/${receipt_id}`,
      {
        params: { include_xml }
      }
    ))
  );
};

/**
 * @ns.route('/QueryDTEState/<int:local_id>/<int:dte_id>')
@ns.param('local_id', 'ID del local que consulta el estado del DTE')
@ns.param('dte_id', 'ID del DTE en la tabla dtes (MySQL)')
class QueryDTEState(Resource):
    @ns.doc('query_dte_state',
            description='**CONSULTA ESTADO DE DTE** en el SII usando solo el DTE ID. '
                       'Este endpoint obtiene automáticamente todos los datos necesarios de la base de datos '
                       '(folio, monto, fecha, RUTs) y consulta el SII por el estado actual del DTE. '
                       'Mucho más simple que la versión anterior - solo necesitas el dte_id.',
            responses={
                200: 'Consulta exitosa - retorna estado del DTE',
                400: 'DTE no tiene folio asignado o faltan datos',
                404: 'DTE no encontrado',
                500: 'Error al consultar estado'
            })
    @jwt_required()
    def get(self, local_id, dte_id):
        '''
        Consulta el estado de un DTE en el SII usando solo el DTE ID.
        
        **SIMPLIFICADO**: Este endpoint automáticamente obtiene todos los datos necesarios
        de la base de datos y consulta el SII. No necesitas pasar parámetros adicionales.
        
        **Cómo funciona:**
        1. Obtiene el registro del DTE desde la tabla `dtes`
        2. Obtiene el recibo asociado para el monto y RUT del cliente
        3. Consulta el SII con estos datos
        4. Retorna el estado actual del DTE
        
        **Datos obtenidos automáticamente:**
        - rut_receptor: RUT del cliente (desde PaymentReceipt.client_rut)
        - folio: Número de folio (desde Dte.folio)
        - total: Monto total (desde PaymentReceipt.subtotal)
        - fecha_dte: Fecha de emisión (desde Dte.created_at)
        - tipo_dte: Tipo de DTE (desde Dte.tipo_dte)
        - rut_emisor: RUT emisor (desde configuración del local en DynamoDB)
        
        **Requisitos previos:**
        - El DTE debe tener un folio asignado (sii_generated=True)
        - El recibo debe tener un client_rut válido
        - El DTE debe pertenecer al local especificado
        
        **Estados posibles (ESTADO):**
        - DOK: Documento recibido, datos coinciden ✅
        - DNK: Documento recibido, datos NO coinciden ⚠️
        - FAU: Documento NO recibido por el SII ❌
        - FAN: Documento anulado ❌
        - FNA: Documento NO autorizado ❌
        - EMP: Empresa NO autorizada ❌
        - TMD/TMC: Existe nota que modifica texto 📝
        - MMD/MMC: Existe nota que modifica montos 💰
        - AND/ANC: Existe nota que anula documento ❌
        
        **Ejemplo de uso:**
        ```
        GET /api/dte/QueryDTEState/1/123
        
        Donde:
        - local_id = 1
        - dte_id = 123
        ```
        
        **Respuesta exitosa:**
        ```json
        {
          "success": true,
          "estado": "DOK",
          "ok": true,
          "glosaEstado": "Documento recibido por el SII",
          "descripcion": "Documento recibido por el SII. Datos coinciden con registros del SII.",
          "responseXml": "<?xml version='1.0'...",
          "query": {
            "rutEmisor": "77651303-2",
            "rutReceptor": "13040552-5",
            "folio": 45,
            "total": 237600,
            "fechaDte": "2025-10-19",
            "tipoDte": 33
          },
          "dte_info": {
            "dte_id": 123,
            "receipt_id": 456,
            "local_id": 1,
            "track_id": "ABC123XYZ"
          }
        }
        ```
        
        **Ventajas vs versión anterior:**
        - ✅ No necesitas buscar manualmente los datos del DTE
        - ✅ No hay riesgo de pasar datos incorrectos
        - ✅ Incluye información del DTE en la respuesta
        - ✅ Más fácil de usar desde el frontend
        - ✅ Validación automática de datos requeridos
        
        **Casos de uso:**
        1. **Verificar estado después de enviar**: Consulta si el SII aceptó el DTE
        2. **Debugging**: Entender por qué un DTE fue rechazado
        3. **Auditoría**: Confirmar estado actual de DTEs históricos
        4. **Sincronización**: Actualizar estado local con SII
        5. **Monitoreo**: Revisar estado de DTEs pendientes
        
        **Errores comunes:**
        
        - 404: DTE no encontrado
          → Verifica que el dte_id sea correcto
        
        - 400: "DTE does not have a folio assigned yet"
          → El DTE aún no ha sido generado completamente (sii_generated=False)
          → Espera a que el workflow complete la generación
        
        - 400: "Receipt does not have a client RUT"
          → El recibo no tiene client_rut asignado
          → Actualiza el recibo con el RUT del cliente
        
        **Notas importantes:**
        - El monto se convierte automáticamente a entero (SII no acepta decimales)
        - La fecha se obtiene del campo created_at del DTE
        - El RUT emisor se obtiene automáticamente de la configuración del local
        - Para boletas (tipo 39, 41) se usa servidor_boleta_rest automáticamente
        '''
        try:
            current_app.logger.info(f"Querying DTE state: local_id={local_id}, dte_id={dte_id}")
            
            # Initialize helper
            helper = DteHelper(local_id=local_id)
            
            # Query DTE state using only the dte_id
            success, data, status = helper.query_dte_state_by_id(dte_id)
            
            if not success:
                current_app.logger.error(f"Failed to query DTE state: {data}")
                return data, status
            
            current_app.logger.info(f"Successfully queried DTE state: estado={data.get('estado')}")
            return data, status
            
        except Exception as e:
            current_app.logger.error(f"Error in QueryDTEState: {str(e)}", exc_info=True)
            return {'error': 'Failed to query DTE state', 'details': str(e)}, 500
 */

export const queryDTEState = async (
  local_id: number,
  dte_id: number
): Promise<AxiosResponse<QueryDteStateResponse>> => {
  return await deduplicateRequest(
    `queryDTEState:${local_id}:${dte_id}`,
    () => retryWithBackoff(() => api.get<QueryDteStateResponse>(
      `/QueryDTEState/${local_id}/${dte_id}`
    ))
  );
};

/**
 * 
 * @ns.route('/GeneratePdf/<int:local_id>/<int:dte_id>')
@ns.param('local_id', 'ID del local')
@ns.param('dte_id', 'ID del DTE en la tabla dtes')
class GeneratePdf(Resource):
    @ns.doc('generate_pdf',
            description='**GENERA PDF IMPRIMIBLE** para un DTE usando solo el dte_id. '
                       'Este endpoint obtiene automáticamente toda la información necesaria de la base de datos '
                       'y genera un PDF formateado en tamaño carta. No requiere parámetros adicionales.',
            responses={
                200: 'PDF generado exitosamente - retorna PDF en base64',
                400: 'DTE XML no disponible',
                404: 'DTE no encontrado',
                408: 'Timeout generando PDF',
                500: 'Error al generar PDF'
            })
    @jwt_required()
    def get(self, local_id, dte_id):
        '''
        Genera un PDF imprimible para un DTE.
        
        **FLUJO AUTOMÁTICO:**
        1. Obtiene el DTE XML desde DynamoDB
        2. Obtiene información del emisor desde configuración
        3. Genera el PDF usando SimpleAPI
        4. Retorna el PDF como bytes
        
        **FORMATOS DISPONIBLES:**
        - **carta**: Tamaño carta estándar (8.5" x 11") - DEFAULT
        - **80mm**: Formato para impresoras térmicas de tickets
        
        **VERSIONES:**
        - **v1**: Template clásico
        - **v2**: Template mejorado - DEFAULT
        
        **VERSIÓN CEDIBLE:**
        - Si cedible=true, genera un PDF con la palabra "CEDIBLE"
        - Usado para facturas que pueden ser transferidas/endosadas
        
        **PARÁMETROS OPCIONALES:**
        Puedes personalizar los siguientes campos del PDF:
        
        - NumeroResolucion: Número de resolución del SII (ej: 80)
        - UnidadSII: Oficina del SII (ej: "Santiago Centro", "ALTO HOSPICIO")
        - FechaResolucion: Fecha de resolución (formato: YYYY-MM-DD, ej: "2021-02-16")
        - Vendedor: Nombre del vendedor (ej: "Juan Pérez")
        - FormaPago: Forma de pago (ej: "EFECTIVO", "TARJETA", "TRANSFERENCIA")
        - CondicionVenta: Condición de venta (ej: "CONTADO", "CREDITO")
        - PropiedadLogo: Estilo del logo ("contain" o "cover")
        
        Si no se especifican, se usan los valores por defecto del emisor.
        
        **REQUISITOS:**
        - El DTE debe estar generado (sii_generated=True)
        - El DTE XML debe estar almacenado en DynamoDB
        - El DTE debe pertenecer al local especificado
        
        **EJEMPLO DE USO BÁSICO:**
        ```
        POST /api/dte/GeneratePdf/1/123
        Content-Type: application/json
        Authorization: Bearer <token>
        
        Body: {}  // Vacío usa defaults
        ```
        
        **EJEMPLO CON PERSONALIZACIÓN:**
        ```json
        {
          "formato": "carta",
          "version": "v2",
          "cedible": false,
          "Vendedor": "María González",
          "FormaPago": "TARJETA",
          "CondicionVenta": "CONTADO"
        }
        ```
        
        **EJEMPLO PARA IMPRESORA TÉRMICA:**
        ```json
        {
          "formato": "80mm",
          "version": "v2",
          "FormaPago": "EFECTIVO"
        }
        ```
        
        **EJEMPLO CEDIBLE:**
        ```json
        {
          "formato": "carta",
          "version": "v2",
          "cedible": true
        }
        ```
        
        **RESPUESTA EXITOSA:**
        La respuesta contiene los bytes del PDF que pueden ser:
        - Descargados directamente por el browser
        - Guardados en el servidor
        - Enviados por email
        - Impresos directamente
        
        ```json
        {
          "success": true,
          "message": "PDF generated successfully",
          "dte_id": 123,
          "folio": 45,
          "tipo_dte": 33,
          "formato": "carta",
          "version": "v2",
          "cedible": false,
          "pdf_size": 89234,
          "content_type": "application/pdf",
          "pdf_content": <binary data>
        }
        ```
        
        **NOTA**: El campo `pdf_content` contiene los bytes del PDF.
        En el frontend, puedes crear un Blob y descargarlo:
        
        ```javascript
        const response = await fetch('/api/dte/GeneratePdf/1/123', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ formato: 'carta' })
        });
        
        const data = await response.json();
        
        // Convert base64 to blob
        const pdfBlob = new Blob([data.pdf_content], { type: 'application/pdf' });
        
        // Create download link
        const url = window.URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `factura_${data.folio}.pdf`;
        a.click();
        ```
        
        **CASOS DE USO:**
        
        1. **Imprimir factura**: Genera PDF carta para imprimir en papel
        2. **Enviar por email**: Genera PDF y adjunta al email del cliente
        3. **Ticket de venta**: Genera PDF 80mm para impresora térmica
        4. **Factura cedible**: Genera versión transferible para endoso
        5. **Archivo**: Genera PDF para guardar en sistema de archivos
        
        **ERRORES COMUNES:**
        
        - 404: "DTE data not found"
          → El DTE XML no está en DynamoDB
          → Verifica que el DTE haya sido generado completamente
        
        - 400: "DTE XML is empty"
          → El DTE existe pero el XML está vacío
          → Regenera el DTE
        
        - 408: Timeout
          → SimpleAPI está tardando mucho
          → Reintentar en unos segundos
        
        **NOTAS TÉCNICAS:**
        - El PDF se genera usando el servicio de SimpleAPI
        - El XML del DTE se obtiene automáticamente de DynamoDB
        - El logo del emisor puede configurarse en el emisor (futuro)
        - El espacio del logo es 180px x 180px
        - El PDF incluye el timbre electrónico del SII
        - El proceso toma aproximadamente 3-10 segundos
        
        **SEGURIDAD:**
        - Solo el local propietario puede generar PDFs de sus DTEs
        - Se valida que el dte_id pertenezca al local_id
        - El JWT token debe ser válido
        Genera un PDF imprimible para un DTE usando solo el dte_id.
        
        **SIMPLIFICADO**: Solo necesitas el local_id y dte_id en la URL.
        Todo lo demás se obtiene automáticamente de la configuración.
        
        **Configuración automática:**
        - Formato: carta (tamaño carta estándar)
        - Versión: v2 (template mejorado)
        - Cedible: false
        - NumeroResolucion: Desde configuración DynamoDB
        - UnidadSII: Mismo valor que Comuna del emisor
        - FechaResolucion: Desde configuración DynamoDB
        - FormaPago: TARJETA (default)
        - CondicionVenta: CREDITO (default)
        
        **Ejemplo de uso:**
        ```
        GET /api/dte/GeneratePdf/1/123
        
        Donde:
        - local_id = 1
        - dte_id = 123
        ```
        
        **Respuesta:**
        ```json
        {
          "success": true,
          "message": "PDF generated successfully",
          "dte_id": 123,
          "folio": 45,
          "tipo_dte": 33,
          "formato": "carta",
          "version": "v2",
          "cedible": false,
          "pdf_size": 89234,
          "content_type": "application/pdf",
          "pdf_content_base64": "JVBERi0xLjQKJeLjz9MKMSAwIG9iago8PC9UeXBlL..."
        }
        ```
        
        **Frontend:**
        ```javascript
        const response = await fetch('/api/dte/GeneratePdf/1/123', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        
        // Convert base64 to blob and download
        const pdfBlob = b64toBlob(data.pdf_content_base64, 'application/pdf');
        const url = window.URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `factura_${data.folio}.pdf`;
        a.click();
        ```
        '''
        try:
            current_app.logger.info(f"Generating PDF for DTE: local_id={local_id}, dte_id={dte_id}")
            
            # Initialize helper
            helper = DteHelper(local_id=local_id)
            
            # Generate PDF with defaults
            success, data, status = helper.generate_pdf_for_dte(
                dte_id=dte_id,
                formato='carta',
                version='v2',
                cedible=False,
                custom_data=None  # Will use defaults from controller
            )
            
            if not success:
                current_app.logger.error(f"Failed to generate PDF: {data}")
                return data, status
            
            # For binary PDF response, we need to return it differently
            # If the frontend needs the actual PDF file, we should use make_response
            from flask import make_response
            import base64
            
            pdf_bytes = data.pop('pdf_content')  # Remove binary data from JSON
            
            # Option 1: Return PDF directly as file
            # Uncomment this if you want to return the PDF file directly
            # response = make_response(pdf_bytes)
            # response.headers['Content-Type'] = 'application/pdf'
            # response.headers['Content-Disposition'] = f'attachment; filename="dte_{dte_id}_folio_{data["folio"]}.pdf"'
            # return response
            
            # Option 2: Return PDF as base64 in JSON (easier for API clients)
            data['pdf_content_base64'] = base64.b64encode(pdf_bytes).decode('utf-8')
            
            current_app.logger.info(f"Successfully generated PDF: dte_id={dte_id}, size={data['pdf_size']} bytes")
            return data, status
            
        except Exception as e:
            current_app.logger.error(f"Error in GeneratePdf: {str(e)}", exc_info=True)
            return {'error': 'Failed to generate PDF', 'details': str(e)}, 500
 */

export interface GeneratePdfOptions {
  formato?: '80mm' | 'carta';
  Ejecutivo?: string;
  Hora?: string;
}

export const generatePdfForDte = async (
  local_id: number,
  dte_id: number,
  options?: GeneratePdfOptions
): Promise<AxiosResponse<GeneratePdfResponse>> => {
  // Always use POST, with optional body
  return await retryWithBackoff(() => api.post<GeneratePdfResponse>(
    `/GeneratePdf/${local_id}/${dte_id}`,
    options || {}
  ));
};
